import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useCities, DEFAULT_CITY, ActiveCity } from '@/hooks/useCities';

type City = string;

interface CityContextType {
  city: City;
  setCity: (city: City) => void;
  isDetecting: boolean;
  cities: ActiveCity[];
  isLoadingCities: boolean;
  detectLocation?: () => void;
}

const CityContext = createContext<CityContextType | undefined>(undefined);

function getClosestCity(lat: number, lng: number, cities: ActiveCity[]): City | null {
  let best: { city: City; dist: number } | null = null;

  for (const c of cities) {
    if (c.lat == null || c.lng == null) continue;
    const dist = Math.sqrt(Math.pow(lat - c.lat, 2) + Math.pow(lng - c.lng, 2));
    if (!best || dist < best.dist) best = { city: c.city, dist };
  }

  return best?.city ?? null;
}

export function CityProvider({ children }: { children: ReactNode }) {
  const { data: cities, isLoading: isLoadingCities } = useCities();
  const [city, setCityState] = useState<City>(() => {
    return localStorage.getItem('preferredCity') || DEFAULT_CITY;
  });
  const [isDetecting, setIsDetecting] = useState(false);
  const [hasAttemptedDetection, setHasAttemptedDetection] = useState(false);

  // If the persisted city is no longer in the feed, fall back to the default
  useEffect(() => {
    if (!cities || cities.length === 0) return;
    if (!cities.some((c) => c.city === city)) {
      setCityState(DEFAULT_CITY);
      localStorage.setItem('preferredCity', DEFAULT_CITY);
    }
  }, [cities, city]);

  // Detect location only when explicitly requested, not on page load
  const detectLocation = useCallback(() => {
    if (hasAttemptedDetection || isDetecting) return;

    setIsDetecting(true);
    setHasAttemptedDetection(true);

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const detectedCity = getClosestCity(
            position.coords.latitude,
            position.coords.longitude,
            cities ?? []
          );
          if (detectedCity) {
            setCityState(detectedCity);
            localStorage.setItem('preferredCity', detectedCity);
          }
          setIsDetecting(false);
        },
        () => {
          // Geolocation denied or failed - keep current selection
          setIsDetecting(false);
        },
        { timeout: 5000 }
      );
    } else {
      setIsDetecting(false);
    }
  }, [cities, hasAttemptedDetection, isDetecting]);

  const setCity = useCallback((newCity: City) => {
    setCityState(newCity);
    localStorage.setItem('preferredCity', newCity);
  }, []);

  return (
    <CityContext.Provider
      value={{
        city,
        setCity,
        isDetecting,
        cities: cities ?? [],
        isLoadingCities,
        detectLocation,
      }}
    >
      {children}
    </CityContext.Provider>
  );
}

export function useCity() {
  const context = useContext(CityContext);
  if (context === undefined) {
    throw new Error('useCity must be used within a CityProvider');
  }
  return context;
}
